/**
 * useRole Hook
 * Custom hook for role-based access control
 */

import { useAuth } from '../context/AuthContext';
import { UserRole, hasRole as checkHasRole, isSuperAdmin as checkIsSuperAdmin, isAdmin as checkIsAdmin } from '../constants/roles';

/**
 * Custom hook to manage user roles and permissions
 * @returns {Object} Role utilities and checks
 */
export const useRole = () => {
  const { user } = useAuth();

  // Get user's role from user object
  // The role might be a number (0, 1, 2) or a string ('SuperAdmin', 'Admin', 'User')
  const getUserRole = () => {
    if (!user) return null;

    // Check both 'role' and 'userRole' fields
    const roleValue = user.role !== undefined ? user.role : user.userRole;

    // If role is already a number, return it
    if (typeof roleValue === 'number') {
      return roleValue;
    }

    // If role is a string, convert to number
    if (typeof roleValue === 'string') {
      const roleUpper = roleValue.toUpperCase();

      // Check for SuperAdmin/SystemAdmin variations
      if (roleUpper === 'SUPERADMIN' || roleUpper === 'SUPER_ADMIN' || roleUpper === 'SYSTEMADMIN' || roleUpper === 'SYSTEM_ADMIN') {
        return UserRole.SUPER_ADMIN;
      }

      // Check for Admin/OrganizationAdmin variations
      if (roleUpper === 'ADMIN' || roleUpper === 'ORGANIZATIONADMIN' || roleUpper === 'ORGANIZATION_ADMIN') {
        return UserRole.ADMIN;
      }

      // Check for User variations
      if (roleUpper === 'USER' || roleUpper === 'NORMALUSER' || roleUpper === 'NORMAL_USER') {
        return UserRole.USER;
      }
    }

    // Default to User role if not specified
    return UserRole.USER;
  };

  const userRole = getUserRole();

  /**
   * Check if user has a specific role or higher
   * @param {number} requiredRole - Required role level
   * @returns {boolean} True if user has required role or higher
   */
  const hasRole = (requiredRole) => {
    if (!user || userRole === null) return false;
    return checkHasRole(userRole, requiredRole);
  };

  /**
   * Check if user is Super Admin
   * @returns {boolean} True if user is super admin
   */
  const isSuperAdmin = () => {
    if (!user || userRole === null) return false;
    return checkIsSuperAdmin(userRole);
  };

  /**
   * Check if user is Admin or higher (Super Admin or Admin)
   * @returns {boolean} True if user is admin or super admin
   */
  const isAdmin = () => {
    if (!user || userRole === null) return false;
    return checkIsAdmin(userRole);
  };

  /**
   * Check if user is a regular User (not admin or super admin)
   * @returns {boolean} True if user is regular user
   */
  const isUser = () => {
    if (!user || userRole === null) return false;
    return userRole === UserRole.USER;
  };

  /**
   * Get role display name
   * @returns {string} Human-readable role name
   */
  const getRoleName = () => {
    if (!user || userRole === null) return 'Unknown';

    switch (userRole) {
      case UserRole.SUPER_ADMIN:
        return 'Super Admin';
      case UserRole.ORGANIZATION_ADMIN:
      case UserRole.ADMIN:
        return 'Organization Admin';
      case UserRole.USER:
        return 'User';
      default:
        return 'Unknown';
    }
  };

  /**
   * Check if user can access a specific route
   * @param {number} requiredRole - Minimum required role for the route
   * @returns {boolean} True if user can access the route
   */
  const canAccessRoute = (requiredRole) => {
    return hasRole(requiredRole);
  };

  /**
   * Check if user can manage organizations (Super Admin only)
   * @returns {boolean} True if user can manage organizations
   */
  const canManageOrganizations = () => {
    return isSuperAdmin();
  };

  /**
   * Check if user can manage users (Admin or Super Admin)
   * @returns {boolean} True if user can manage users
   */
  const canManageUsers = () => {
    return isAdmin();
  };

  /**
   * Check if user can manage forms (Admin or Super Admin)
   * @returns {boolean} True if user can manage forms
   */
  const canManageForms = () => {
    return isAdmin();
  };

  /**
   * Get user's organization ID
   * @returns {number|null} Organization ID or null
   */
  const getOrganizationId = () => {
    return user?.organizationId || null;
  };

  return {
    // Current user role
    userRole,
    user,

    // Role checking functions
    hasRole,
    isSuperAdmin,
    isAdmin,
    isUser,
    getRoleName,

    // Permission checking functions
    canAccessRoute,
    canManageOrganizations,
    canManageUsers,
    canManageForms,

    // Organization
    getOrganizationId,

    // Role constants for comparison
    roles: UserRole,
  };
};

export default useRole;
